import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BellRing, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { API, errText, useAuth } from "@/lib/auth";
import { ago, money } from "@/components/market/Shared";

export default function Alerts() {
  const { profile, refresh } = useAuth();
  const loc = useLocation();
  const [list, setList] = useState(null);
  const [checker, setChecker] = useState(null);
  const [form, setForm] = useState({ asset: loc.state?.asset || "bitcoin", direction: "above", price: "" });
  const seen = useRef(new Set());
  const [coins, setCoins] = useState([]);
  useEffect(() => { axios.get(`${API}/crypto/markets`, { params: { limit: 250 } }).then((r) => setCoins(r.data.assets || [])).catch(() => {}); }, []);
  const load = useCallback(() => axios.get(`${API}/me/alerts`).then((r) => {
    setList(r.data.alerts); setChecker(r.data.checker);
    r.data.alerts.filter((a) => a.status === "triggered" && !a.seen && !seen.current.has(a.id)).forEach((a) => {
      seen.current.add(a.id);
      toast.success(`${a.symbol} is ${a.direction} ${money(a.price)} (hit ${money(a.triggered_price)})`);
      if (profile?.settings?.browser_alerts && "Notification" in window && Notification.permission === "granted")
        new Notification("BTC Odds Watch price alert", { body: `${a.symbol} ${a.direction} ${money(a.price)}` });
    });
  }).catch(() => {}), [profile]);
  useEffect(() => { if (profile) { load(); const id = setInterval(load, 30000); return () => clearInterval(id); } }, [profile, load]);

  if (profile === undefined) return null;
  if (!profile) return (
    <div className="glass p-8 text-center" data-testid="alerts-signin">
      <BellRing className="w-8 h-8 mx-auto text-[var(--cyan)]" />
      <h1 className="font-head font-bold text-2xl mt-3">Price alerts</h1>
      <p className="text-sm text-slate-400 mt-2">Create a free account to get alerted when a coin crosses your price.</p>
      <Link to="/account" className="btn-primary inline-block mt-5" data-testid="alerts-signin-btn">Sign in or create account</Link>
    </div>
  );

  const add = async () => {
    try {
      await axios.post(`${API}/me/alerts`, { ...form, price: Number(form.price) });
      toast.success("Alert created"); setForm({ ...form, price: "" }); load();
    } catch (e) { toast.error(errText(e)); }
  };
  const del = async (id) => { await axios.delete(`${API}/me/alerts/${id}`); load(); };
  const setBrowser = async (on) => {
    if (on && "Notification" in window && Notification.permission !== "granted") await Notification.requestPermission();
    await axios.put(`${API}/me/settings`, { browser_alerts: on }); refresh();
  };
  const markSeen = async () => { await axios.post(`${API}/me/alerts/seen`); load(); };

  return (
    <div className="space-y-4" data-testid="alerts-page">
      <h1 className="font-head font-bold text-3xl tracking-tight">Price alerts</h1>
      <section className="glass p-5">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <Input value={form.asset} onChange={(e) => setForm({ ...form, asset: e.target.value })} placeholder="BTC, ETH, Dogecoin…" list="alert-coins" className="h-11 bg-[var(--bg)] border-[var(--line)]" data-testid="alert-asset-input" />
          <datalist id="alert-coins">{coins.map((c) => <option key={c.id} value={c.id}>{c.symbol} · {c.name}</option>)}</datalist>
          <select value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })} className="h-11 rounded-md bg-[var(--bg)] border border-[var(--line)] px-3 text-sm" data-testid="alert-direction-select">
            <option value="above">Goes above</option><option value="below">Goes below</option>
          </select>
          <Input type="number" inputMode="decimal" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Price in USD" className="h-11 bg-[var(--bg)] border-[var(--line)] font-num" data-testid="alert-price-input" />
          <button onClick={add} className="btn-primary" data-testid="alert-create-btn">Create alert</button>
        </div>
        <p className="text-xs text-slate-500 mt-3">Your plan: {profile.limits.alerts_max} active alerts. Type a ticker (BTC), a name (Bitcoin) or pick from the list.</p>
      </section>
      <section className="glass p-5 flex items-center justify-between gap-3" data-testid="alert-prefs">
        <div><div className="font-semibold text-sm">Browser notifications</div>
          <p className="text-xs text-slate-400">Prices are checked on the server every 60s{checker?.last_run ? ` (last check ${ago(checker.last_run)})` : ""}. Pop-up notifications appear while this app is open in your browser. Email and push notifications are not set up yet.</p></div>
        <Switch checked={!!profile.settings?.browser_alerts} onCheckedChange={setBrowser} data-testid="alert-browser-switch" />
      </section>
      <section className="glass divide-y divide-[var(--line)]" data-testid="alerts-list">
        {list?.length ? list.map((a) => (
          <div key={a.id} className="flex items-center gap-3 p-4 text-sm" data-testid={`alert-row-${a.id}`}>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${a.status === "triggered" ? "bg-emerald-500/15 text-emerald-300" : "bg-[var(--cyan)]/10 text-[var(--cyan)]"}`}>{a.status.toUpperCase()}</span>
            <div className="flex-1 min-w-0">{a.name} {a.direction} <b className="font-num">{money(a.price)}</b>
              <div className="text-xs text-slate-500">{a.status === "triggered" ? `Hit ${money(a.triggered_price)} · ${ago(a.triggered_at)}` : `Set at ${money(a.price_when_set)} · ${ago(a.created_at)}`}</div></div>
            <button onClick={() => del(a.id)} aria-label="Delete" data-testid={`alert-delete-${a.id}`}><Trash2 className="w-4 h-4 text-slate-500 hover:text-red-400" /></button>
          </div>
        )) : <p className="p-6 text-sm text-slate-400" data-testid="alerts-empty">No alerts yet. Create one above.</p>}
      </section>
      {list?.some((a) => a.status === "triggered" && !a.seen) && <button onClick={markSeen} className="text-xs text-slate-400 underline" data-testid="alerts-mark-seen">Mark triggered alerts as seen</button>}
    </div>
  );
}
